import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LocaleContext } from 'designSystem/context';

export const useQuery = <T extends { error: boolean }>(onFetch: () => Promise<T>) => {
  const { locale } = useContext(LocaleContext);
  const isMounted = useRef(true);
  const [isError, setError] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [response, setResponse] = useState<T | null>(null);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  });

  const handleFetch = useCallback(
    (keepLastResponse?: boolean) => {
      if (!isMounted.current) {
        return;
      }

      setLoading(true);
      setError(false);

      if (!keepLastResponse) {
        setResponse(null);
      }

      onFetch()
        .then(fetchResponse => {
          if (fetchResponse.error) {
            handleError();
          } else {
            handleSuccess(fetchResponse);
          }
        })
        .catch(() => {
          handleError();
        });
    },
    [onFetch],
  );

  useEffect(() => {
    handleFetch();
  }, [handleFetch, locale]);

  const handleSuccess = (fetchResponse: T) => {
    if (!isMounted.current) {
      return;
    }

    setTimeout(() => {
      setLoading(false);
      setResponse(fetchResponse);
    }, 300);
  };

  const handleError = () => {
    if (!isMounted.current) {
      return;
    }

    setError(true);
    setLoading(false);
  };

  return {
    error: isError,
    handleFetch,
    loading: isLoading,
    response,
  };
};
